/**
 * Promotion ceremony (08 §2.9, 04 §4.2): the old insignia, the new, and - on the ranks that
 * carry one - the choice of one perk from three. CONTINUE appears only once no choice is owed.
 */

import './ceremony.css';
import type { PerkId, RangerRecord } from '../../../meta/types';
import { box, button, el, errorMessage } from '../dom';
import { RANK_MERIT, rankAbbr, rankIndex } from '../text';
import { rankInsignia } from '../widgets/insignia';
import type { Step, StepEnv } from './shell';
import { continueButton, stepShell } from './shell';

/** A ranger holds at most six perks (04 §4.2): no run of choices can be longer. */
const MAX_CHOICES = 6;

/**
 * The rank he held before this mission: the contract reports only the new one, so it is read
 * back from the merit he had (02 §11), and is always at least one step below the new rank. Pure.
 */
export function rankBefore(promotedTo: number, merit: number, meritGained: number): number {
  const before = merit - meritGained;
  let rank = 0;
  RANK_MERIT.forEach((needed, i) => {
    if (before >= needed) rank = i;
  });
  return Math.max(0, Math.min(rankIndex(promotedTo) - 1, rank));
}

function insigniaBox(rank: number, caption: string): HTMLElement {
  return box('menu-promo-rank', box('menu-promo-insignia', rankInsignia(rank)), el('div', 'menu-promo-abbr', caption));
}

/** Perks still on offer: never one he already holds, so a stale list cannot loop. */
function open(choices: readonly PerkId[] | null | undefined, ranger: RangerRecord): PerkId[] {
  return (choices ?? []).filter((perk) => !ranger.perks.includes(perk));
}

function perkCard(env: StepEnv, perk: PerkId, onPick: (perk: PerkId) => void): HTMLButtonElement {
  const d = env.ctx.describe(perk);
  const card = button('', () => onPick(perk), 'menu-perk-card');
  card.append(el('span', 'menu-perk-name', d.name), el('span', 'menu-perk-text menu-prose', d.text));
  return card;
}

export const promotionStep: Step = (env, next) => {
  const after = env.input.aftermath;
  const ranger = env.ranger;
  const shell = stepShell(after !== null && after.promotedTo !== null ? 'PROMOTION' : 'PERK CHOICE');
  if (after === null || ranger === null) return { el: shell.el, focus: null };

  if (after.promotedTo !== null) {
    const was = rankBefore(after.promotedTo, after.ranger.merit, after.meritGained);
    shell.content.append(
      box(
        'menu-promo-ranks',
        insigniaBox(was, rankAbbr(was)),
        el('span', 'menu-promo-arrow', '>>'),
        insigniaBox(after.promotedTo, rankAbbr(after.promotedTo)),
      ),
      el('div', 'menu-promo-line', `PROMOTED TO ${rankAbbr(after.promotedTo)}`),
    );
  }

  const choiceBox = el('div', 'menu-col');
  shell.content.append(choiceBox);
  const go = continueButton(next);
  let firstFocus: HTMLElement = go;
  let rounds = 0;

  const finish = (line: HTMLElement): void => {
    choiceBox.replaceChildren(line);
    shell.actions.replaceChildren(go);
    go.focus();
  };

  const offer = (choices: PerkId[]): void => {
    const current = env.ranger ?? ranger;
    const cards = choices.map((perk) =>
      perkCard(env, perk, (picked) => {
        let updated: RangerRecord;
        try {
          updated = env.ctx.api.choosePerk(current.id, picked);
        } catch (err) {
          finish(el('div', 'menu-error', errorMessage(err)));
          return;
        }
        env.ranger = updated;
        rounds++;
        const owed = open(updated.pendingPerkChoices[0], updated);
        if (owed.length > 0 && rounds < MAX_CHOICES) offer(owed);
        else finish(el('div', 'menu-promo-taken menu-good', `PERK TAKEN: ${env.ctx.describe(picked).name}`));
      }),
    );
    choiceBox.replaceChildren(
      el('div', 'menu-promo-prompt', 'CHOOSE ONE. THE CHOICE IS PERMANENT.'),
      box('menu-perk-cards', ...cards),
    );
    shell.actions.replaceChildren();
    const first = cards[0];
    if (first !== undefined) {
      firstFocus = first;
      first.focus();
    }
  };

  const initial = open(after.perkChoices, ranger);
  if (initial.length > 0) offer(initial);
  else shell.actions.append(go);
  return { el: shell.el, focus: firstFocus };
};
